import Field from "./field";
import Entity from "./entity";
import Documents from "./documents";
import DocumentValue from "./document-value";
import { template } from "../utils";
import ILinkedInactive from "../interface/linked-inactive";
import ILinkedFilterOption from "../interface/linked-filter-option";
import ILinkedFilter from "../interface/linked-filter";
import ILinkedFieldOption from "../interface/linked-field-option";
import IConfigLinked from "../interface/field-cfg-linked";
import IField from "../interface/field";
import IEntity from "../interface/entity";
import IDocument from "../interface/document";
import LinkedInactiveType from "../enum/linked-inactive-type";
import FieldType from "../enum/field-type";

class LinkedFieldInstance {

    /* Public Methods */

    public async getOptions(fieldId: number, record: IDocument | null = null): Promise<ILinkedFieldOption[]> {
        const field: IField | null = Field.get(fieldId);
        if (field && field.uiType === FieldType.linked) {
            const entity: IEntity = Entity.get(field.foreignerEcn) as IEntity;
            let docs: IDocument[];
            if (record) {
                /* This is in case of collection or record */
                docs = await Documents.loadDocuments(field.foreignerEcn);
                docs = await LinkedField.filter(field, record, docs);
            } else {
                /* This is in case of regular field options uploading */
                docs = await Documents.loadDocuments(field.foreignerEcn, {
                    fields: field.foreignerFCNs
                });
            }
            return docs.map((doc: IDocument) => {
                return {
                    id: doc.id as number,
                    name: template(field.linkedTemplate, entity, field.foreignerFCNs, doc)
                } as ILinkedFieldOption;
            });
        }
        return [];
    }

    public getFilterOptions(originId: number, foreignId: number): ILinkedFilterOption[] {
        const result: ILinkedFilterOption[] = [];
        const from: IEntity | null = Entity.get(originId);
        const to: IEntity | null = Entity.get(foreignId);
        if (from && to) {
            const linkedFrom: IField[] = Entity.getFieldsByType(originId, [FieldType.linked]);
            const linkedTo: IField[] = Entity.getFieldsByType(foreignId, [FieldType.linked]);
            /* Looking for a "ToRecord" type filters */
            const toRecord: IField[] = linkedTo.filter((f: IField) => f.foreignerEcn === originId);
            toRecord.forEach((field: IField) => result.push({
                originField: null,
                foreignField: field
            }));
            /* Looking for a "ToField" type filters */
            linkedFrom.forEach((from: IField) => {
                linkedTo.forEach((to: IField) => {
                    if (from.foreignerEcn === to.foreignerEcn) {
                        result.push({
                            originField: from,
                            foreignField: to
                        });
                    }
                });;
            });
        }
        return result;
    }

    /* Private Public Methods */

    public isActive(fieldId: number, record: IDocument): ILinkedInactive[] {
        const field: IField | null = Field.get(fieldId);
        if (field) {
            const reasons: ILinkedInactive[] = [];
            const config: IConfigLinked = field.uiConfig as IConfigLinked;
            /* First checking self filters */
            if (config.filters) {
                config.filters.forEach((filter: ILinkedFilter) => {
                    if (filter.originFieldId) {
                        const origin: IField | null = Field.get(filter.originFieldId);
                        if (origin) {
                            if (!DocumentValue.getLinkedId(record[`c${origin.linkedRelation[0]}`])) {
                                reasons.push({ type: LinkedInactiveType.NoFilterValue, field: origin });
                            }
                        }
                    }
                });
            }
            /* Then checking sibling linked fields filters */
            Entity.getFieldsByType(field.entityId, [FieldType.linked]).forEach((sibling: IField) => {
                const config: IConfigLinked = sibling.uiConfig as IConfigLinked;
                if (sibling.id !== field.id && config.filters) {
                    config.filters.forEach((filter: ILinkedFilter) => {
                        if (filter.originFieldId === field.id) {
                            if (DocumentValue.getLinkedId(record[`c${sibling.linkedRelation[0]}`])) {
                                reasons.push({ type: LinkedInactiveType.UsedByOther, field: sibling });
                            }
                        }
                    });
                }
            });
            return reasons;
        } else {
            return [];
        }
    }

    private async filter(field: IField, record: IDocument, options: IDocument[]): Promise<IDocument[]> {
        const config: IConfigLinked = field.uiConfig as IConfigLinked;
        let result: IDocument[] = [...options];
        /* First checking the filters */
        if (config && config.filters && config.filters.length) {
            config.filters.forEach((filter: ILinkedFilter) => {
                const foreignField: IField | null = Field.get(filter.foreignFieldId);
                if (foreignField) {
                    let originValue: number = 0;
                    if (filter.originFieldId) {
                        const originField: IField | null = Field.get(filter.originFieldId);
                        if (originField) {
                            if (record[`c${originField.linkedRelation[0]}`] instanceof Array) {
                                originValue = (record[`c${originField.linkedRelation[0]}`] as number[])[0];
                            }
                        } else {
                            return;
                        }
                    } else {
                        originValue = record.id as number;
                    }
                    result = result.filter((doc: IDocument) => {
                        let foreignValue: number = 0;
                        if (doc[`c${foreignField.linkedRelation[0]}`] instanceof Array) {
                            foreignValue = (doc[`c${foreignField.linkedRelation[0]}`] as number[])[0];
                        }
                        return originValue === foreignValue;
                    });
                }
            });
        }
        /* Then checking the repetitions */
        if (config && config.forbidRepetitions) {
            /* We can use regular .loadDocuments call here because of smart caching */
            const records: IDocument[] = await Documents.loadDocuments(field.entityId, {});
            const values: (number | null)[] = records.map((doc: IDocument) => {
                if (doc.id !== record.id && doc[`c${field.linkedRelation[0]}`] instanceof Array) {
                    return (doc[`c${field.linkedRelation[0]}`] as number[])[0];
                }
                return null;
            });
            result = result.filter((doc: IDocument) => !values.includes(doc.id as number));
        }
        return result;
    }

}

const LinkedField = new LinkedFieldInstance();

export default LinkedField;